import { computed, effect, signal } from "@preact/signals";
import * as _ from "underscore";

import type { PositionRange } from "readerText/tokens";

export default class ReaderViewStore {
  /** Range of the first visible word or phrase on the page. */
  readonly firstRange = signal<PositionRange | undefined>(undefined);
  /** Range of the last visible word or phrase on the page. */
  readonly lastRange = signal<PositionRange | undefined>(undefined);
  readonly fromPosition = signal(0);
  readonly pageLayoutUpdatedSequence = signal(0);
  /** Hidden while a new page is prepared, shown (with a fade) once laid out. */
  readonly textVisible = signal(true);
  /** The page turn bringing in the coming page, which slides in that way */
  readonly pageTurn = signal<"next" | "previous" | undefined>(undefined);

  readonly hasFirstSpan = computed(() => this.firstRange.value !== undefined);
  readonly hasLastSpan = computed(() => this.lastRange.value !== undefined);
  readonly firstPosition = computed(() => this.firstRange.value?.from);
  readonly lastPosition = computed(() => this.lastRange.value?.to);

  setFirstRange(firstRange: PositionRange | undefined) {
    this.firstRange.value = firstRange;
  }

  setLastRange(lastRange: PositionRange | undefined) {
    this.lastRange.value = lastRange;
  }

  setFromPosition(fromPosition: number) {
    this.fromPosition.value = fromPosition;
  }

  setPageTurn(pageTurn: "next" | "previous" | undefined) {
    this.pageTurn.value = pageTurn;
  }

  setTextVisible(textVisible: boolean) {
    this.textVisible.value = textVisible;
  }

  notifyPageLayoutUpdated() {
    // peek() so callers running inside effects don't subscribe themselves to
    // the signal they're bumping (signals-core "Cycle detected").
    this.pageLayoutUpdatedSequence.value =
      this.pageLayoutUpdatedSequence.peek() + 1;
  }

  onChange<T>(
    selector: (store: ReaderViewStore) => T,
    callback: (value: T, previousValue: T) => void
  ) {
    let previousValue = selector(this);
    return effect(() => {
      const value = selector(this);
      if (!_.isEqual(value, previousValue)) {
        const oldValue = previousValue;
        previousValue = value;
        callback(value, oldValue);
      }
    });
  }

  onPageLayoutUpdated(callback: () => void) {
    let previousSequence = this.pageLayoutUpdatedSequence.value;
    return effect(() => {
      const sequence = this.pageLayoutUpdatedSequence.value;
      if (sequence !== previousSequence) {
        previousSequence = sequence;
        callback();
      }
    });
  }
}
